/**
 * DATOS — mocks centralizados del panel de vigilancia LIQUITECH.
 *
 * Única fuente de datos simulados mientras no exista el `liquitech-integration-service`.
 * Forma y estados tomados de `sql/01_schema_liquitech.sql`. Los servicios consumen
 * ESTE archivo (no los componentes); cuando el backend esté disponible, se reemplaza
 * la fuente en los services sin tocar la UI.
 *
 * Escenario: fecha de referencia 2026-10-05. Convivencia multi-factor
 * (LIQUITECH + Mente) para ejercitar la dimensión "factor" del panel.
 */
import {
  Alerta,
  AsientoContable,
  AuditoriaEvento,
  Cesion,
  Factura,
  InboxEvento,
  OutboxEvento,
  Tercero,
} from '../models';

/** Fecha de referencia del escenario (hoy). */
export const HOY = '2026-10-05';
const d = (fecha: string, hora = '09:00:00') => `${fecha}T${hora}-05:00`;

// ---------------------------------------------------------------------------
// Terceros: factores (multi-factor) y proveedores
// ---------------------------------------------------------------------------
export const FACTORES: Tercero[] = [
  {
    id: 1,
    nit: '901777888',
    tipo: 'factor',
    nombre: 'LIQUITECH S.A.S.',
    codigoErp: 'FAC-LQT',
    habilitadoFactoring: false,
    activo: true,
    creadoEn: d('2026-07-01'),
    actualizadoEn: d('2026-07-01'),
  },
  {
    id: 2,
    nit: '900888999',
    tipo: 'factor',
    nombre: 'Mente Factoring S.A.',
    codigoErp: 'FAC-MNT',
    habilitadoFactoring: false,
    activo: true,
    creadoEn: d('2026-07-01'),
    actualizadoEn: d('2026-07-01'),
  },
];

export const PROVEEDORES: Tercero[] = [
  prov(10, '900111222', 'Distribuidora Andina S.A.S.'),
  prov(11, '901222333', 'Comercializadora El Roble Ltda.'),
  prov(12, '860333444', 'Alimentos del Valle S.A.'),
  prov(13, '901444555', 'Textiles Caribe S.A.S.'),
  prov(14, '900555666', 'Logística Pacífico Ltda.'),
  prov(15, '901666777', 'Insumos Médicos Norte S.A.S.'),
];

function prov(id: number, nit: string, nombre: string): Tercero {
  return {
    id,
    nit,
    tipo: 'proveedor',
    nombre,
    codigoErp: `PRV-${id}`,
    habilitadoFactoring: true,
    activo: true,
    creadoEn: d('2026-07-10'),
    actualizadoEn: d('2026-09-20'),
  };
}

export const TERCEROS: Tercero[] = [...FACTORES, ...PROVEEDORES];

// ---------------------------------------------------------------------------
// Facturas — ciclo CU001→CU004
// ---------------------------------------------------------------------------
export const FACTURAS: Factura[] = [
  // Expuestas HOY (alimentan el KPI "facturas expuestas hoy")
  factura(100, 10, 'FV-2026-4501', 18_450_000, '2026-09-05', '2026-11-04', 'expuesta', d(HOY, '06:30:00')),
  factura(101, 11, 'FV-2026-3310', 7_820_500, '2026-09-10', '2026-11-09', 'expuesta', d(HOY, '06:30:00')),
  factura(102, 12, 'FV-2026-9087', 42_300_000, '2026-08-28', '2026-10-27', 'expuesta', d(HOY, '06:30:00')),
  factura(103, 13, 'FV-2026-2214', 5_110_000, '2026-09-18', '2026-11-17', 'expuesta', d(HOY, '06:30:00')),
  factura(104, 14, 'FV-2026-7765', 26_900_000, '2026-09-01', '2026-10-31', 'expuesta', d(HOY, '06:30:00')),
  // Expuestas días previos
  factura(105, 15, 'FV-2026-1190', 12_750_000, '2026-08-20', '2026-10-19', 'expuesta', d('2026-10-04', '06:30:00')),
  factura(106, 10, 'FV-2026-4480', 9_300_000, '2026-08-15', '2026-10-14', 'expuesta', d('2026-10-03', '06:30:00')),
  // En análisis (el proveedor la seleccionó en LIQUITECH)
  factura(107, 11, 'FV-2026-3280', 33_600_000, '2026-08-22', '2026-10-21', 'en_analisis', d('2026-10-02', '06:30:00')),
  factura(108, 12, 'FV-2026-9012', 15_040_000, '2026-08-30', '2026-10-29', 'en_analisis', d('2026-10-01', '06:30:00')),
  // Cedidas (ciclo completo)
  factura(109, 13, 'FV-2026-2150', 28_100_000, '2026-08-10', '2026-10-09', 'cedida', d('2026-09-28', '06:30:00')),
  factura(110, 14, 'FV-2026-7700', 61_500_000, '2026-08-05', '2026-10-04', 'cedida', d('2026-09-26', '06:30:00')),
  factura(111, 15, 'FV-2026-1120', 8_900_000, '2026-08-12', '2026-10-11', 'cedida', d('2026-09-25', '06:30:00')),
  factura(112, 10, 'FV-2026-4402', 47_250_000, '2026-08-01', '2026-09-30', 'cedida', d('2026-09-20', '06:30:00')),
  // Elegibles aún no expuestas
  factura(113, 11, 'FV-2026-3400', 6_430_000, '2026-09-25', '2026-11-24', 'elegible', null),
  factura(114, 12, 'FV-2026-9150', 21_780_000, '2026-09-22', '2026-11-21', 'elegible', null),
  // Salidas del flujo
  factura(115, 13, 'FV-2026-2260', 4_200_000, '2026-09-12', '2026-11-11', 'rechazada', d('2026-09-30', '06:30:00')),
  factura(116, 14, 'FV-2026-7810', 10_050_000, '2026-07-30', '2026-09-28', 'no_elegible', null, 'Nota crédito abierta sobre la factura'),
];

function factura(
  id: number,
  proveedorId: number,
  numeroFactura: string,
  valorTotal: number,
  fechaEmision: string,
  fechaVencimiento: string,
  estado: Factura['estado'],
  expuestaEn: string | null,
  motivoNoElegible: string | null = null,
): Factura {
  const proveedor = PROVEEDORES.find((p) => p.id === proveedorId)!;
  return {
    id,
    proveedorId,
    proveedorNombre: proveedor.nombre,
    numeroFactura,
    cufe: `CUFE${id}${proveedorId}A1B2C3`,
    documentoErp: `CxP-${id}`,
    valorTotal,
    moneda: 'COP',
    fechaEmision,
    fechaVencimiento,
    estado,
    motivoNoElegible,
    expuestaEn,
    creadoEn: d(fechaEmision, '08:00:00'),
    actualizadoEn: expuestaEn ?? d(fechaEmision, '08:00:00'),
  };
}

// ---------------------------------------------------------------------------
// Cesiones — una por factura en flujo de cesión
// ---------------------------------------------------------------------------
export const CESIONES: Cesion[] = [
  // Reclasificada + conciliada (ciclo cerrado)
  cesion(200, 112, 1, 'LQT-CES-8841', 'conciliada', 47_250_000, 45_500_000, '2026-09-21', '2026-09-22', '2026-09-22', '2026-09-23', 'tesoreria.ltorres'),
  cesion(201, 109, 1, 'LQT-CES-8902', 'reclasificada', 28_100_000, 27_050_000, '2026-09-29', '2026-09-30', '2026-09-30', null, null),
  // Desembolsadas HOY (alimentan KPIs "cesiones del día" y "desembolsos del día")
  cesion(202, 110, 1, 'LQT-CES-9001', 'desembolsada', 61_500_000, 59_200_000, '2026-10-04', d(HOY, '08:10:00'), d(HOY, '08:40:00'), null, null),
  // El factor negoció 8.56 M frente a 8.9 M del ERP → dispara 'monto_no_cuadra' (alerta 401).
  cesion(203, 111, 2, 'MNT-CES-5521', 'desembolsada', 8_560_000, 8_300_000, '2026-10-04', d(HOY, '08:20:00'), d(HOY, '08:55:00'), null, null),
  // Aprobada hoy, aún sin desembolso
  cesion(204, 107, 1, 'LQT-CES-9033', 'aprobada', 33_600_000, null, '2026-10-02', d(HOY, '07:50:00'), null, null, null),
  // Solicitada (en curso)
  cesion(205, 108, 1, 'LQT-CES-9050', 'solicitada', 15_040_000, null, '2026-10-03', null, null, null, null),
  // Rechazada (salida)
  cesion(206, 115, 2, 'MNT-CES-5540', 'rechazada', 4_200_000, null, '2026-09-29', null, null, null, null),
];

function cesion(
  id: number,
  facturaId: number,
  factorId: number,
  referenciaExterna: string,
  estado: Cesion['estado'],
  valorNegociado: number,
  valorDesembolsado: number | null,
  fechaSolicitud: string,
  fechaCesion: string | null,
  fechaDesembolso: string | null,
  conciliadoEn: string | null,
  conciliadoPor: string | null,
): Cesion {
  const fac = FACTURAS.find((f) => f.id === facturaId)!;
  const factor = FACTORES.find((t) => t.id === factorId)!;
  return {
    id,
    facturaId,
    numeroFactura: fac.numeroFactura,
    proveedorNombre: fac.proveedorNombre,
    factorId,
    factorNombre: factor.nombre,
    referenciaExterna,
    estado,
    valorNegociado,
    valorDesembolsado,
    fechaSolicitud: fechaSolicitud.includes('T') ? fechaSolicitud : d(fechaSolicitud, '07:00:00'),
    fechaCesion,
    fechaDesembolso,
    conciliadoEn: conciliadoEn ? d(conciliadoEn, '10:00:00') : null,
    conciliadoPor,
    creadoEn: d(fechaSolicitud.slice(0, 10), '07:00:00'),
    actualizadoEn: fechaDesembolso ?? fechaCesion ?? d(fechaSolicitud.slice(0, 10), '07:00:00'),
  };
}

// ---------------------------------------------------------------------------
// Asientos contables — reclasificación proveedor→factor (RF-07)
// ---------------------------------------------------------------------------
export const ASIENTOS: AsientoContable[] = [
  // Confirmado (cesión conciliada 200)
  asiento(300, 200, 'confirmado', 'RC-2026-0455', 45_500_000, 112 /*prov*/, 1 /*factor*/, d('2026-09-22', '11:00:00')),
  // Generado (cesión reclasificada 201)
  asiento(301, 201, 'generado', 'RC-2026-0470', 27_050_000, 109, 1, d('2026-09-30', '11:30:00')),
  // Pendiente (cesión desembolsada 202, aún sin asiento → vigilar)
  asiento(302, 202, 'pendiente', null, 59_200_000, 110, 1, null),
  // Error de generación (cesión 203)
  asiento(303, 203, 'error', null, 8_560_000, 111, 2, null, 'Tercero factor sin cuenta contable homologada'),
];

function asiento(
  id: number,
  cesionId: number,
  estado: AsientoContable['estado'],
  documentoErp: string | null,
  valor: number,
  terceroDebitoRefFacturaId: number,
  terceroCreditoId: number,
  generadoEn: string | null,
  ultimoError: string | null = null,
): AsientoContable {
  const fac = FACTURAS.find((f) => f.id === terceroDebitoRefFacturaId)!;
  return {
    id,
    cesionId,
    estado,
    documentoErp,
    valor,
    terceroDebitoId: fac.proveedorId,
    terceroCreditoId,
    generadoEn,
    ultimoError,
    creadoEn: d('2026-09-30', '11:00:00'),
    actualizadoEn: generadoEn ?? d('2026-09-30', '11:00:00'),
  };
}

// ---------------------------------------------------------------------------
// Alertas — inconsistencias que vigila Tesorería/TIC
// ---------------------------------------------------------------------------
export const ALERTAS: Alerta[] = [
  {
    id: 400,
    tipo: 'cesion_sin_asiento',
    severidad: 'critica',
    estado: 'abierta',
    titulo: 'Cesión desembolsada sin asiento contable',
    detalle: 'La cesión LQT-CES-9001 (factura FV-2026-7700) fue desembolsada hace más de 6 h y su asiento sigue pendiente.',
    facturaId: 110,
    cesionId: 202,
    datos: { horasSinAsiento: 6, valor: 59_200_000 },
    creadoEn: d(HOY, '14:45:00'),
  },
  {
    id: 401,
    tipo: 'monto_no_cuadra',
    severidad: 'critica',
    estado: 'abierta',
    titulo: 'Monto del factor no coincide con el ERP',
    detalle: 'El valor negociado de MNT-CES-5521 difiere del valor contable de la factura en el ERP.',
    facturaId: 111,
    cesionId: 203,
    datos: { valorErp: 8_900_000, valorFactor: 8_560_000, diferencia: 340_000 },
    creadoEn: d(HOY, '09:12:00'),
  },
  {
    id: 402,
    tipo: 'desembolso_sin_conciliar',
    severidad: 'advertencia',
    estado: 'abierta',
    titulo: 'Desembolso recibido sin conciliar',
    detalle: 'El desembolso de LQT-CES-9001 no ha sido conciliado por Tesorería.',
    facturaId: 110,
    cesionId: 202,
    datos: { valorDesembolsado: 59_200_000 },
    creadoEn: d(HOY, '09:05:00'),
  },
  {
    id: 403,
    tipo: 'duplicado_bloqueado',
    severidad: 'info',
    estado: 'abierta',
    titulo: 'Reenvío duplicado bloqueado por idempotencia',
    detalle: 'Un webhook repetido de LIQUITECH (evento evt-ext-7781) chocó con la unicidad y no se procesó dos veces.',
    facturaId: null,
    cesionId: 201,
    datos: { eventoIdExterno: 'evt-ext-7781' },
    creadoEn: d('2026-10-04', '18:22:00'),
  },
  {
    id: 404,
    tipo: 'reenvio_fallido',
    severidad: 'advertencia',
    estado: 'reconocida',
    titulo: 'Outbox agotó reintentos de exposición',
    detalle: 'El evento factura.expuesta de FV-2026-1190 agotó sus reintentos hacia LIQUITECH.',
    facturaId: 105,
    cesionId: null,
    datos: { intentos: 8 },
    creadoEn: d('2026-10-04', '07:15:00'),
    reconocidoEn: d('2026-10-04', '08:00:00'),
    reconocidoPor: 'tic.jmolina',
  },
  {
    id: 405,
    tipo: 'sla_incumplido',
    severidad: 'critica',
    estado: 'resuelta',
    titulo: 'Disponibilidad por debajo del acuerdo (99.5%)',
    detalle: 'La integración estuvo intermitente entre 02:10 y 02:40; se recuperó dentro del RTO.',
    facturaId: null,
    cesionId: null,
    datos: { disponibilidadMes: 99.42 },
    creadoEn: d('2026-10-02', '02:40:00'),
    reconocidoEn: d('2026-10-02', '03:00:00'),
    reconocidoPor: 'tic.jmolina',
    resueltoEn: d('2026-10-02', '09:30:00'),
    resueltoPor: 'tic.jmolina',
  },
];

// ---------------------------------------------------------------------------
// Eventos de integración (outbox / inbox) — observabilidad técnica (ops/TIC)
// ---------------------------------------------------------------------------
export const OUTBOX: OutboxEvento[] = [
  outbox(500, 'factura.expuesta', 100, 'enviado', 1, d(HOY, '06:30:10')),
  outbox(501, 'factura.expuesta', 104, 'enviado', 1, d(HOY, '06:30:12')),
  outbox(502, 'factura.expuesta', 105, 'error', 8, null, 'Timeout contra el endpoint de LIQUITECH'),
  outbox(503, 'factura.retirada', 116, 'pendiente', 0, null),
];

function outbox(
  id: number,
  tipo: string,
  agregadoId: number,
  estado: OutboxEvento['estado'],
  intentos: number,
  enviadoEn: string | null,
  ultimoError: string | null = null,
): OutboxEvento {
  return {
    id,
    tipo,
    agregadoTipo: 'factura',
    agregadoId,
    estado,
    intentos,
    ultimoError,
    disponibleEn: d(HOY, '06:30:00'),
    enviadoEn,
    creadoEn: d(HOY, '06:30:00'),
  };
}

export const INBOX: InboxEvento[] = [
  inbox(600, 'evt-ext-7760', 'cesion.confirmada', 'procesado', 202, 1, d(HOY, '08:10:05')),
  inbox(601, 'evt-ext-7765', 'desembolso.realizado', 'procesado', 202, 1, d(HOY, '08:40:05')),
  inbox(602, 'evt-ext-7781', 'cesion.confirmada', 'duplicado', 201, 1, d('2026-10-04', '18:22:05')),
  inbox(603, 'evt-ext-7790', 'desembolso.realizado', 'recibido', null, 0, null),
];

function inbox(
  id: number,
  eventoIdExterno: string,
  tipo: string,
  estado: InboxEvento['estado'],
  cesionId: number | null,
  intentos: number,
  procesadoEn: string | null,
): InboxEvento {
  return {
    id,
    eventoIdExterno,
    tipo,
    estado,
    cesionId,
    intentos,
    ultimoError: null,
    recibidoEn: procesadoEn ?? d(HOY, '08:00:00'),
    procesadoEn,
  };
}

// ---------------------------------------------------------------------------
// Auditoría — trazabilidad (RF-09)
// ---------------------------------------------------------------------------
export const AUDITORIA: AuditoriaEvento[] = [
  audit(700, 'factura.expuesta', 'factura', 100, 'sistema', d(HOY, '06:30:10')),
  audit(701, 'cesion.confirmada', 'cesion', 202, 'factor', d(HOY, '08:10:05')),
  audit(702, 'cesion.desembolsada', 'cesion', 202, 'factor', d(HOY, '08:40:05')),
  audit(703, 'cesion.desembolsada', 'cesion', 203, 'factor', d(HOY, '08:55:00')),
  audit(704, 'asiento.generado', 'asiento', 301, 'sistema', d('2026-09-30', '11:30:00')),
  audit(705, 'cesion.conciliada', 'cesion', 200, 'tesoreria.ltorres', d('2026-09-23', '10:00:00')),
];

function audit(
  id: number,
  evento: string,
  entidadTipo: string,
  entidadId: number,
  actor: string,
  ocurridoEn: string,
): AuditoriaEvento {
  return { id, evento, entidadTipo, entidadId, actor, datos: null, ocurridoEn };
}
